import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getAllUsers } from "../../redux/actions/user";
import { DataGrid } from "@mui/x-data-grid";
import { AiOutlineDelete } from "react-icons/ai";
import { Button } from "@mui/material";
import styles from "../../styles/styles";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import Loader from "../Layout/Loader";

const AllUsers = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.user);
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Filter and Sort persistence
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    // Load saved filters only if navigating back (not on fresh page load)
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageReload = navigationEntry && navigationEntry.type === 'reload';
    
    if (!isPageReload) {
      const savedFilter = sessionStorage.getItem('allUsersFilterModel');
      const savedSort = sessionStorage.getItem('allUsersSortModel');
      
      if (savedFilter) {
        setFilterModel(JSON.parse(savedFilter));
      }
      if (savedSort) {
        setSortModel(JSON.parse(savedSort));
      }
    } else {
      // Clear filters on page reload
      sessionStorage.removeItem('allUsersFilterModel');
      sessionStorage.removeItem('allUsersSortModel');
    }
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        await dispatch(getAllUsers());
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, [dispatch]);

  const handleDelete = async (id) => {
    await axios
      .delete(`${server}/user/delete-user/${id}`, { withCredentials: true })
      .then((res) => {
        toast.success(res.data.message);
        dispatch(getAllUsers());
      })
      .catch((error) => toast.error("Failed to delete user"));
  };

  const handleCreditApprovalUpdate = async (userId, creditApproved, creditLimit) => {
    try {
      const response = await axios.post(
        `${server}/user/update-credit-approval/${userId}`,
        { creditApproved, creditLimit },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      dispatch(getAllUsers());
    } catch (error) {
      toast.error("Failed to update credit approval");
    }
  };

  const columns = [
    { field: "id", headerName: "User ID", width: 150 },
    { field: "name", headerName: "Name", width: 130 },
    { field: "email", headerName: "Email", width: 130 },
    { field: "role", headerName: "User Role", width: 130 },
    { field: "joinedAt", headerName: "Joined At", width: 130 },
    { field: "gstNumber", headerName: "GST Number", width: 130 },
    {
      field: "viewTransactions",
      headerName: "View Transactions",
      width: 150,
      renderCell: (params) => (
        <Link
          to={`/admin/user-transactions/${params.id}`}
          style={{ color: "#007bff", textDecoration: "none" }}
        >
          View Transactions
        </Link>
      ),
    },
    {
      field: "creditApproved",
      headerName: "Credit Approved",
      width: 150,
      editable: true,
      renderCell: (params) => (
        <select
          value={params.row.creditApproved}
          onChange={async (e) => {
            const updatedValue = e.target.value === "true";
            await handleCreditApprovalUpdate(
              params.id,
              updatedValue,
              params.row.creditLimit
            );
          }}
        >
          <option value={true}>True</option>
          <option value={false}>False</option>
        </select>
      ),
    },
    {
      field: "creditLimit",
      headerName: "Credit Limit (Rs)",
      width: 130,
      editable: true,
    },
    {
      field: "update",
      headerName: "Update",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const { id, row } = params;
        return (
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              handleCreditApprovalUpdate(id, row.creditApproved, row.creditLimit)
            }
          >
            Update
          </Button>
        );
      },
    },
    {
      field: "delete",
      headerName: "Delete User",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          onClick={() => {
            setUserId(params.id);
            setOpen(true);
          }}
        >
          <AiOutlineDelete size={20} />
        </Button>
      ),
    },
  ];

  const rows = [];
  users &&
    users.forEach((item) => {
      rows.push({
        id: item._id,
        name: item.name,
        email: item.email,
        role: item.role,
        joinedAt: item.createdAt.slice(0, 10),
        gstNumber: item.gstNumber || "N/A",
        creditApproved: item.creditApproved,
        creditLimit: item.creditLimit,
      });
    });

  return (
    <div className="w-full">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <h3 className="text-[22px] font-Poppins pb-2">All Users</h3>
          <div className="w-full min-h-[45vh] bg-white rounded">
        <div className="datagrid-scroll-wrapper">
          <div className="table-inner-container" style={{ minWidth: "1510px" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              disableSelectionOnClick
              autoHeight
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
                sessionStorage.setItem('allUsersFilterModel', JSON.stringify(newFilterModel));
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                setSortModel(newSortModel);
                sessionStorage.setItem('allUsersSortModel', JSON.stringify(newSortModel));
              }}
              sx={{
                fontSize: "0.8rem",
                border: "none",
                "& .MuiDataGrid-cell": { fontSize: "0.8rem", padding: "0.5rem" },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: "0.8rem",
                  padding: "0.5rem",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "visible !important",
                },
              }}
            />
          </div>
        </div>
      </div>
      {open && (
        <div className="w-full fixed top-0 left-0 z-[999] bg-[#00000039] flex items-center justify-center h-screen">
          <div className="w-[95%] 800px:w-[40%] min-h-[20vh] bg-white rounded shadow p-5">
            <div className="w-full flex justify-end cursor-pointer">
              <RxCross1 size={25} onClick={() => setOpen(false)} />
            </div>
            <h3 className="text-[25px] text-center py-5 font-Poppins text-[#000000cb]">
              Are you sure you want to delete this user?
            </h3>
            <div className="w-full flex items-center justify-center">
              <div
                className={`${styles.button} text-white text-[18px] !h-[42px] mr-4`}
                onClick={() => setOpen(false)}
              >
                Cancel
              </div>
              <div
                className={`${styles.button} text-white text-[18px] !h-[42px] ml-4`}
                onClick={() => {
                  setOpen(false);
                  handleDelete(userId);
                }}
              >
                Confirm
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AllUsers;
